import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
  Pagination,
  Alert,
  Tooltip,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  InputAdornment,
  Snackbar,
} from "@mui/material";
import {
  Delete,
  Edit,
  Visibility,
  Sync,
  Search,
  Clear,
  VideoCall,
  ContentCopy,
  OpenInNew,
  Group,
  Warning,
  CheckCircle,
  PlayArrow,
  People,
} from "@mui/icons-material";
import {
  zoomMeetingsAPI,
  ZoomMeetingFilters,
  CreateZoomMeetingPayload,
  UpdateZoomMeetingPayload,
  ZoomMeetingSettings,
} from "../../services/adminApi";

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface ZoomMeetingSettingsFull {
  hostVideo: boolean;
  participantVideo: boolean;
  joinBeforeHost: boolean;
  muteUponEntry: boolean;
  waitingRoom: boolean;
  approvalType: number;
  audio: "both" | "telephony" | "voip";
  autoRecording: "local" | "cloud" | "none";
}

interface ZoomMeeting {
  _id: string;
  topic: string;
  agenda: string;
  meetingType: 1 | 2 | 3 | 8;
  startTime: string;
  duration: number;
  timezone: string;
  password: string;
  hostEmail: string;
  joinUrl: string;
  startUrl: string;
  zoomMeetingId: string;
  status: "scheduled" | "started" | "ended" | "cancelled";
  settings: ZoomMeetingSettingsFull;
  allowedRoles: ("admin" | "member" | "all")[];
  isPublic: boolean;
  maxParticipants: number;
  zoomSynced: boolean;
  createdBy: { name: string; email: string };
  createdAt: string;
}

const ADMIN_COLORS = {
  sidebar: "#0A1628",
  accent: "#1565C0",
  bg: "#F0F7FF",
  text: "#0A1628",
  textMuted: "#78909C",
};

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    color:
      | "default"
      | "warning"
      | "success"
      | "error"
      | "info"
      | "primary"
      | "secondary";
  }
> = {
  scheduled: { label: "Scheduled", color: "info" },
  started: { label: "Live", color: "success" },
  ended: { label: "Ended", color: "default" },
  cancelled: { label: "Cancelled", color: "error" },
};

const MEETING_TYPE_LABELS: Record<number, string> = {
  1: "Instant",
  2: "Scheduled",
  3: "Recurring (no fixed time)",
  8: "Recurring (fixed time)",
};

const TIMEZONES = [
  "Asia/Beirut",
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Tokyo",
  "Asia/Kolkata",
  "Australia/Sydney",
];

// â”€â”€ Helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString("en-LB", {
    timeZone: "Asia/Beirut",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function toLocalDateTimeString(iso: string) {
  if (!iso) return "";
  // Display picker in Lebanon time (Asia/Beirut)
  const beirut = new Date(iso).toLocaleString("sv-SE", { timeZone: "Asia/Beirut" });
  return beirut.slice(0, 16); // "YYYY-MM-DDTHH:MM"
}

const emptyCreateForm: CreateZoomMeetingPayload = {
  topic: "",
  agenda: "",
  meetingType: 2,
  startTime: "",
  duration: 60,
  timezone: "Asia/Beirut",
  password: "",
  isPublic: false,
  allowedRoles: ["all"],
  maxParticipants: 100,
  settings: {
    hostVideo: true,
    participantVideo: true,
    joinBeforeHost: false,
    muteUponEntry: true,
    waitingRoom: true,
    approvalType: 0,
    audio: "both",
    autoRecording: "none",
  },
};

// â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const AdminZoomMeetings: React.FC = () => {
  const [meetings, setMeetings] = useState<ZoomMeeting[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const [filters, setFilters] = useState<ZoomMeetingFilters>({
    page: 1,
    limit: 15,
    status: undefined,
    search: "",
  });
  const [searchInput, setSearchInput] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);

  const [selectedMeeting, setSelectedMeeting] = useState<ZoomMeeting | null>(null);
  const [createForm, setCreateForm] =
    useState<CreateZoomMeetingPayload>(emptyCreateForm);
  const [editForm, setEditForm] = useState<UpdateZoomMeetingPayload>({});
  const [participants, setParticipants] = useState<unknown[]>([]);
  const [actionLoading, setActionLoading] = useState(false);

  // â”€â”€ Fetch â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await zoomMeetingsAPI.list(filters);
      setMeetings(res.data.data || []);
      setTotal(res.data.total || 0);
      setZoomConfigured(res.data.zoomConfigured ?? false);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load meetings");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // â”€â”€ Handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const showSnack = (message: string, severity: "success" | "error" = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleSearch = () =>
    setFilters((f) => ({ ...f, page: 1, search: searchInput }));

  const handleResetFilters = () => {
    setSearchInput("");
    setFilters({ page: 1, limit: 15 });
  };

  const handleCreateSubmit = async () => {
    if (!createForm.topic || !createForm.startTime || !createForm.duration) {
      showSnack("Topic, start time and duration are required", "error");
      return;
    }
    setActionLoading(true);
    try {
      await zoomMeetingsAPI.create(createForm);
      showSnack(
        zoomConfigured
          ? "Meeting created and synced with Zoom!"
          : "Meeting saved locally. Add Zoom API credentials to sync."
      );
      setCreateOpen(false);
      setCreateForm(emptyCreateForm);
      fetchMeetings();
    } catch (err: any) {
      showSnack(err.response?.data?.message || "Failed to create meeting", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditOpen = (m: ZoomMeeting) => {
    setSelectedMeeting(m);
    setEditForm({
      topic: m.topic,
      agenda: m.agenda,
      startTime: toLocalDateTimeString(m.startTime),
      duration: m.duration,
      timezone: m.timezone,
      password: m.password,
      status: m.status,
      allowedRoles: m.allowedRoles,
      isPublic: m.isPublic,
      maxParticipants: m.maxParticipants,
      settings: {
        hostVideo: m.settings.hostVideo,
        participantVideo: m.settings.participantVideo,
        joinBeforeHost: m.settings.joinBeforeHost,
        muteUponEntry: m.settings.muteUponEntry,
        waitingRoom: m.settings.waitingRoom,
        approvalType: m.settings.approvalType,
        audio: m.settings.audio as ZoomMeetingSettings["audio"],
        autoRecording: m.settings.autoRecording as ZoomMeetingSettings["autoRecording"],
      },
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedMeeting) return;
    setActionLoading(true);
    try {
      const payload: UpdateZoomMeetingPayload = {
        ...editForm,
        startTime: editForm.startTime
          ? new Date(editForm.startTime).toISOString()
          : undefined,
      };
      await zoomMeetingsAPI.update(selectedMeeting._id, payload);
      showSnack("Meeting updated successfully");
      setEditOpen(false);
      fetchMeetings();
    } catch (err: any) {
      showSnack(err.response?.data?.message || "Failed to update meeting", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMeeting) return;
    setActionLoading(true);
    try {
      await zoomMeetingsAPI.remove(selectedMeeting._id);
      showSnack("Meeting deleted");
      setDeleteOpen(false);
      fetchMeetings();
    } catch (err: any) {
      showSnack(err.response?.data?.message || "Failed to delete meeting", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSync = async (meeting: ZoomMeeting) => {
    try {
      await zoomMeetingsAPI.sync(meeting._id);
      showSnack("Synced with Zoom API");
      fetchMeetings();
    } catch (err: any) {
      showSnack(
        err.response?.data?.message || "Sync failed â€” check Zoom credentials",
        "error"
      );
    }
  };

  const handleViewParticipants = async (meeting: ZoomMeeting) => {
    setSelectedMeeting(meeting);
    setParticipants([]);
    setParticipantsOpen(true);
    try {
      const res = await zoomMeetingsAPI.participants(meeting._id);
      setParticipants((res.data.data as any)?.participants || []);
    } catch {
      setParticipants([]);
    }
  };

  const copyToClipboard = (text: string, label = "Link") => {
    navigator.clipboard
      .writeText(text)
      .then(() => showSnack(`${label} copied to clipboard`));
  };

  // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const totalPages = Math.ceil(total / (filters.limit || 15));

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: ADMIN_COLORS.bg, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <VideoCall sx={{ color: ADMIN_COLORS.accent, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={ADMIN_COLORS.text}>
              Zoom Meetings
            </Typography>
            <Typography variant="body2" color={ADMIN_COLORS.textMuted}>
              {total} meeting{total !== 1 ? "s" : ""} total
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<VideoCall />}
          onClick={() => setCreateOpen(true)}
          sx={{
            bgcolor: ADMIN_COLORS.accent,
            color: "#fff",
            fontWeight: 700,
            borderRadius: "10px",
            px: 2.5,
            py: 1,
            textTransform: "none",
            fontSize: "0.92rem",
            boxShadow: "0 4px 14px rgba(21,101,192,0.35)",
            "&:hover": {
              bgcolor: "#0D47A1",
              boxShadow: "0 6px 20px rgba(21,101,192,0.45)",
              transform: "translateY(-1px)",
            },
            transition: "all .2s ease",
          }}
        >
          Add Zoom Meeting
        </Button>
      </Box>

      {/* Zoom API status banner */}
      {!zoomConfigured && (
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2, borderRadius: 2 }}>
          <strong>Zoom API not configured.</strong> Meetings are saved locally. Add{" "}
          <code>ZOOM_ACCOUNT_ID</code>, <code>ZOOM_CLIENT_ID</code>, and{" "}
          <code>ZOOM_CLIENT_SECRET</code> to the backend <code>.env</code> to enable real
          Zoom links.
        </Alert>
      )}
      {zoomConfigured && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2, borderRadius: 2 }}>
          Zoom API connected â€” meetings are synced automatically.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }} elevation={0} variant="outlined">
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search topic, agenda, host..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status || ""}
                onChange={(e) =>
                  setFilters((f) => ({
                    ...f,
                    page: 1,
                    status: (e.target.value as any) || undefined,
                  }))
                }
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="started">Live</MenuItem>
                <MenuItem value="ended">Ended</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              sx={{
                bgcolor: ADMIN_COLORS.sidebar,
                "&:hover": { bgcolor: "#304F42" },
              }}
            >
              Search
            </Button>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer
        component={Paper}
        elevation={0}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: ADMIN_COLORS.sidebar }}>
            <TableRow>
              {[
                "Topic",
                "Date & Time",
                "Duration",
                "Status",
                "Zoom Sync",
                "Links",
                "Actions",
              ].map((h) => (
                <TableCell key={h} sx={{ color: "#fff", fontWeight: 700, py: 1.5 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton height={28} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : meetings.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 6, color: ADMIN_COLORS.textMuted }}
                >
                  <VideoCall
                    sx={{
                      fontSize: 48,
                      opacity: 0.3,
                      display: "block",
                      mx: "auto",
                      mb: 1,
                    }}
                  />
                  No meetings found. Create your first Zoom meeting.
                </TableCell>
              </TableRow>
            ) : (
              meetings.map((m) => (
                <TableRow key={m._id} hover>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {m.topic}
                    </Typography>
                    {m.agenda && (
                      <Typography
                        variant="caption"
                        color={ADMIN_COLORS.textMuted}
                        noWrap
                        display="block"
                      >
                        {m.agenda}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(m.startTime)}</Typography>
                    <Typography variant="caption" color={ADMIN_COLORS.textMuted}>
                      {m.timezone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{m.duration} min</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_CONFIG[m.status]?.label || m.status}
                      color={STATUS_CONFIG[m.status]?.color || "default"}
                    />
                  </TableCell>
                  <TableCell>
                    {m.zoomSynced ? (
                      <Chip
                        size="small"
                        icon={<CheckCircle />}
                        label="Synced"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Tooltip title="Not synced â€” click Sync to create Zoom link">
                        <Chip
                          size="small"
                          icon={<Warning />}
                          label="Local only"
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {m.joinUrl ? (
                        <>
                          <Tooltip title="Copy join link">
                            <IconButton
                              size="small"
                              onClick={() => copyToClipboard(m.joinUrl, "Join link")}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Open join link">
                            <IconButton
                              size="small"
                              component="a"
                              href={m.joinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Typography variant="caption" color={ADMIN_COLORS.textMuted}>
                          No link yet
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="View details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedMeeting(m);
                            setViewOpen(true);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditOpen(m)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sync with Zoom">
                        <IconButton
                          size="small"
                          onClick={() => handleSync(m)}
                          color="primary"
                        >
                          <Sync fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View participants">
                        <IconButton
                          size="small"
                          onClick={() => handleViewParticipants(m)}
                          color="info"
                        >
                          <People fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {m.startUrl && (
                        <Tooltip title="Start meeting (host)">
                          <IconButton
                            size="small"
                            color="success"
                            component="a"
                            href={m.startUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedMeeting(m);
                            setDeleteOpen(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination
            count={totalPages}
            page={filters.page || 1}
            onChange={(_, p) => setFilters((f) => ({ ...f, page: p }))}
            color="primary"
          />
        </Box>
      )}

      {/* â”€â”€ Create Dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: ADMIN_COLORS.text }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VideoCall sx={{ color: ADMIN_COLORS.accent }} />
            Create Zoom Meeting
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Topic *"
                fullWidth
                size="small"
                value={createForm.topic}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, topic: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Agenda / Description"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={createForm.agenda}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, agenda: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Start Date & Time *"
                fullWidth
                size="small"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={
                  createForm.startTime
                    ? toLocalDateTimeString(createForm.startTime)
                    : ""
                }
                onChange={(e) =>
                  setCreateForm((f) => ({
                    ...f,
                    startTime: new Date(e.target.value).toISOString(),
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Duration (minutes) *"
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                value={createForm.duration}
                onChange={(e) =>
                  setCreateForm((f) => ({
                    ...f,
                    duration: parseInt(e.target.value) || 60,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  label="Timezone"
                  value={createForm.timezone || "Asia/Beirut"}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, timezone: e.target.value }))
                  }
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz} value={tz}>
                      {tz}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Meeting Type</InputLabel>
                <Select
                  label="Meeting Type"
                  value={createForm.meetingType || 2}
                  onChange={(e) =>
                    setCreateForm((f) => ({
                      ...f,
                      meetingType: Number(e.target.value) as any,
                    }))
                  }
                >
                  {Object.entries(MEETING_TYPE_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={Number(k)}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Password"
                fullWidth
                size="small"
                value={createForm.password}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, password: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Max Participants"
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                value={createForm.maxParticipants}
                onChange={(e) =>
                  setCreateForm((f) => ({
                    ...f,
                    maxParticipants: parseInt(e.target.value) || 100,
                  }))
                }
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                color={ADMIN_COLORS.textMuted}
                gutterBottom
              >
                Access Control
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={createForm.isPublic}
                    onChange={(e) =>
                      setCreateForm((f) => ({ ...f, isPublic: e.target.checked }))
                    }
                  />
                }
                label="Public (show on member portal)"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                color={ADMIN_COLORS.textMuted}
                gutterBottom
              >
                Meeting Settings
              </Typography>
              <Grid container spacing={1}>
                {[
                  { key: "hostVideo", label: "Host video on" },
                  { key: "participantVideo", label: "Participant video on" },
                  { key: "joinBeforeHost", label: "Allow join before host" },
                  { key: "muteUponEntry", label: "Mute on entry" },
                  { key: "waitingRoom", label: "Waiting room" },
                ].map(({ key, label }) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={
                            !!(
                              createForm.settings?.[
                                key as keyof typeof createForm.settings
                              ]
                            )
                          }
                          onChange={(e) =>
                            setCreateForm((f) => ({
                              ...f,
                              settings: { ...f.settings, [key]: e.target.checked },
                            }))
                          }
                        />
                      }
                      label={label}
                    />
                  </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Auto Recording</InputLabel>
                    <Select
                      label="Auto Recording"
                      value={createForm.settings?.autoRecording || "none"}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          settings: {
                            ...f.settings,
                            autoRecording: e.target.value as
                              | "local"
                              | "cloud"
                              | "none",
                          },
                        }))
                      }
                    >
                      <MenuItem value="none">None</MenuItem>
                      <MenuItem value="local">Local</MenuItem>
                      <MenuItem value="cloud">Cloud</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={actionLoading}
            sx={{ bgcolor: ADMIN_COLORS.accent, "&:hover": { bgcolor: ADMIN_COLORS.accent } }}
          >
            {actionLoading ? "Creating..." : "Create Meeting"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* â”€â”€ Edit Dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: ADMIN_COLORS.text }}>
          Edit Meeting
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Topic"
                fullWidth
                size="small"
                value={editForm.topic || ""}
                onChange={(e) => setEditForm((f) => ({ ...f, topic: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Agenda"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={editForm.agenda || ""}
                onChange={(e) => setEditForm((f) => ({ ...f, agenda: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Start Date & Time"
                fullWidth
                size="small"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editForm.startTime || ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, startTime: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Duration (min)"
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 1 }}
                value={editForm.duration || ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    duration: parseInt(e.target.value) || undefined,
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={editForm.status || ""}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, status: e.target.value as any }))
                  }
                >
                  <MenuItem value="scheduled">Scheduled</MenuItem>
                  <MenuItem value="started">Started</MenuItem>
                  <MenuItem value="ended">Ended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Password"
                fullWidth
                size="small"
                value={editForm.password || ""}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, password: e.target.value }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                label="Max Participants"
                fullWidth
                size="small"
                type="number"
                value={editForm.maxParticipants || ""}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    maxParticipants: parseInt(e.target.value),
                  }))
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isPublic ?? false}
                    onChange={(e) =>
                      setEditForm((f) => ({ ...f, isPublic: e.target.checked }))
                    }
                  />
                }
                label="Public"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="subtitle2"
                color={ADMIN_COLORS.textMuted}
                gutterBottom
              >
                Settings
              </Typography>
              <Grid container spacing={1}>
                {[
                  { key: "hostVideo", label: "Host video" },
                  { key: "participantVideo", label: "Participant video" },
                  { key: "joinBeforeHost", label: "Join before host" },
                  { key: "muteUponEntry", label: "Mute on entry" },
                  { key: "waitingRoom", label: "Waiting room" },
                ].map(({ key, label }) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!(editForm.settings as any)?.[key]}
                          onChange={(e) =>
                            setEditForm((f) => ({
                              ...f,
                              settings: { ...f.settings, [key]: e.target.checked },
                            }))
                          }
                        />
                      }
                      label={label}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={actionLoading}
            sx={{ bgcolor: ADMIN_COLORS.accent, "&:hover": { bgcolor: "#c4845e" } }}
          >
            {actionLoading ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* â”€â”€ View Dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Meeting Details</DialogTitle>
        <Divider />
        <DialogContent>
          {selectedMeeting && (
            <Grid container spacing={1.5}>
              {[
                { label: "Topic", value: selectedMeeting.topic },
                { label: "Agenda", value: selectedMeeting.agenda || "â€”" },
                {
                  label: "Zoom Meeting ID",
                  value: selectedMeeting.zoomMeetingId || "Not synced",
                },
                { label: "Host Email", value: selectedMeeting.hostEmail || "â€”" },
                {
                  label: "Start Time",
                  value: formatDateTime(selectedMeeting.startTime),
                },
                { label: "Duration", value: `${selectedMeeting.duration} minutes` },
                { label: "Timezone", value: selectedMeeting.timezone },
                { label: "Password", value: selectedMeeting.password || "None" },
                {
                  label: "Max Participants",
                  value: String(selectedMeeting.maxParticipants),
                },
                {
                  label: "Type",
                  value:
                    MEETING_TYPE_LABELS[selectedMeeting.meetingType] || "Scheduled",
                },
                {
                  label: "Zoom Synced",
                  value: selectedMeeting.zoomSynced ? "Yes" : "No",
                },
              ].map(({ label, value }) => (
                <Grid size={{ xs: 6 }} key={label}>
                  <Typography variant="caption" color={ADMIN_COLORS.textMuted}>
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {value}
                  </Typography>
                </Grid>
              ))}
              {selectedMeeting.joinUrl && (
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" color={ADMIN_COLORS.textMuted}>
                    Join URL
                  </Typography>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-all", flex: 1 }}
                    >
                      {selectedMeeting.joinUrl}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() =>
                        copyToClipboard(selectedMeeting.joinUrl, "Join link")
                      }
                    >
                      <ContentCopy fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      component="a"
                      href={selectedMeeting.joinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              )}
              {selectedMeeting.startUrl && (
                <Grid size={{ xs: 12 }}>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    component="a"
                    href={selectedMeeting.startUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      bgcolor: ADMIN_COLORS.accent,
                      "&:hover": { bgcolor: "#c4845e" },
                      mt: 1,
                    }}
                    fullWidth
                  >
                    Start Meeting (Host)
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* â”€â”€ Participants Dialog â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Dialog
        open={participantsOpen}
        onClose={() => setParticipantsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <People sx={{ color: ADMIN_COLORS.accent }} />
            Participants â€” {selectedMeeting?.topic}
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {participants.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Group
                sx={{
                  fontSize: 48,
                  opacity: 0.3,
                  display: "block",
                  mx: "auto",
                  mb: 1,
                }}
              />
              <Typography color={ADMIN_COLORS.textMuted}>
                {zoomConfigured
                  ? "No participant data yet (meeting may not have ended)"
                  : "Zoom API not configured â€” participant data unavailable"}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Join Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.map((p: any, i) => (
                    <TableRow key={i}>
                      <TableCell>{p.name || "â€”"}</TableCell>
                      <TableCell>{p.user_email || p.email || "â€”"}</TableCell>
                      <TableCell>
                        {p.join_time ? formatDateTime(p.join_time) : "â€”"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParticipantsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* â”€â”€ Delete Confirm â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Meeting</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{selectedMeeting?.topic}</strong>? This will also remove the
            meeting from Zoom if it was synced.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={actionLoading}
          >
            {actionLoading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminZoomMeetings;


